import { getAccessToken, staleGoogleTokenError } from './google-auth';

/**
 * «Открыть в Google Docs» для любого отчёта, который уже умеет собирать Word:
 * готовый .docx заливается в Drive с преобразованием в документ Google.
 * Так таблицы, скрины и отступы переезжают как есть — второй вёрстки на
 * Docs API под каждый отчёт не нужно.
 *
 * drive.file видит только файлы, созданные самим приложением, — чужой Drive
 * этим токеном не прочитать.
 */
export const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

const UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id';
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export type DocxFile = { blob: Blob; filename: string };

/**
 * Токен берём сразу по клику, до сборки файла: сборка идёт десятки секунд,
 * и окно входа Google после неё браузер уже заблокирует.
 */
export function requestGoogleDocsToken(): Promise<string> {
  return getAccessToken(DRIVE_FILE_SCOPE);
}

async function uploadError(res: Response): Promise<Error> {
  const stale = staleGoogleTokenError(res.status);
  if (stale) return stale;
  let reason = '';
  try {
    const body = await res.json();
    reason = body?.error?.errors?.[0]?.reason ?? body?.error?.status ?? '';
  } catch {
    // тело не JSON — хватит кода ответа
  }
  if (reason === 'accessNotConfigured' || reason === 'SERVICE_DISABLED') {
    return new Error('В проекте Google не включён Drive API — документ не создать');
  }
  return new Error(`Google Drive не принял файл: ${res.status}${reason ? ` (${reason})` : ''}`);
}

/** Заливает .docx и возвращает адрес документа Google. */
export async function uploadDocxAsGoogleDoc(token: string, file: DocxFile): Promise<string> {
  const name = file.filename.replace(/\.docx$/i, '');
  const form = new FormData();
  form.append(
    'metadata',
    new Blob([JSON.stringify({ name, mimeType: 'application/vnd.google-apps.document' })], {
      type: 'application/json',
    }),
  );
  form.append('file', new Blob([file.blob], { type: DOCX_MIME }), file.filename);

  const res = await fetch(UPLOAD_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) throw await uploadError(res);
  const { id } = (await res.json()) as { id: string };
  return `https://docs.google.com/document/d/${id}/edit`;
}

/**
 * Заливает файлы по очереди и пробует открыть каждый во вкладке. Вкладку
 * после долгой сборки браузер может заблокировать — поэтому адреса
 * возвращаются, а экран показывает их ссылками.
 */
export async function openDocxInGoogleDocs(token: string, files: DocxFile[]): Promise<string[]> {
  const urls: string[] = [];
  for (const file of files) {
    const url = await uploadDocxAsGoogleDoc(token, file);
    urls.push(url);
    window.open(url, '_blank', 'noopener');
  }
  return urls;
}

export function downloadDocx({ blob, filename }: DocxFile): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
